import {
  Document as DocumentRPC,
  File as FileType,
  ProposalPdfRequest,
  type QuoteDocument,
  URLObject,
} from '@kalos/kalos-rpc';

import {
  DocumentClientService,
  FileClientService,
  getMimeType,
  ProposalClientService,
  S3ClientService,
} from '../../../tools/helpers';

export async function fetchProposalPdfBlob(jobId: number, quoteDocumentId: number): Promise<Blob> {
  const request = ProposalPdfRequest.create();
  request.jobId = jobId;
  request.quoteDocumentId = quoteDocumentId;
  const response = await ProposalClientService.getProposalPreview(request);
  if (!response?.url) {
    throw new Error('Failed to generate proposal preview URL');
  }
  const pdfResponse = await fetch(response.url);
  if (!pdfResponse.ok) {
    throw new Error('Failed to fetch PDF content');
  }
  return pdfResponse.blob();
}

/** Resolves the S3 file row for the signed contract PDF, or null if not available. */
export async function resolveSignedContractGalleryFile(
  quoteDocument: QuoteDocument,
): Promise<FileType | null> {
  if (!quoteDocument.documentId) return null;

  const documentReq = DocumentRPC.create();
  documentReq.id = quoteDocument.documentId;
  const documentRes = await DocumentClientService.Get(documentReq);
  if (!documentRes?.fileId) return null;

  const fileReq = FileType.create();
  fileReq.id = documentRes.fileId;
  const fileRes = await FileClientService.Get(fileReq);
  if (!fileRes?.bucket || !fileRes.name) return null;

  const mimeType =
    fileRes.mimeType && fileRes.mimeType !== '' ? fileRes.mimeType : getMimeType(fileRes.name);

  return FileType.create({
    id: fileRes.id,
    name: fileRes.name,
    bucket: fileRes.bucket,
    mimeType,
  });
}

export async function fetchSignedContractBlob(quoteDocument: QuoteDocument): Promise<Blob> {
  const galleryFile = await resolveSignedContractGalleryFile(quoteDocument);
  if (!galleryFile) {
    throw new Error('Signed contract file is not available');
  }
  const urlObj = URLObject.create({
    bucket: galleryFile.bucket,
    key: galleryFile.name,
  });
  const downloadUrl = await S3ClientService.GetDownloadURL(urlObj);
  if (!downloadUrl?.url) {
    throw new Error('Failed to get download URL');
  }
  const response = await fetch(downloadUrl.url);
  if (!response.ok) {
    throw new Error('Failed to download file');
  }
  return response.blob();
}

export function downloadBlobToDevice(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
